import { useState, useEffect } from "react";
import { Volume2 } from "lucide-react";
import VTurbPlayer from "@/components/VTurbPlayer";
import LeadDialog from "@/components/LeadDialog";
import { LANDING } from "@/constants/testIds";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_luxury-china-travel/artifacts/lp0vcfgh_Camada%207.png";

export default function LandingPage() {
  const [open, setOpen] = useState(false);
  const [showCta, setShowCta] = useState(false);
  const openForm = () => setOpen(true);

  useEffect(() => {
    const t = setTimeout(() => setShowCta(true), 30000);
    return () => clearTimeout(t);
  }, []);

  return (
    <main
      data-testid={LANDING.page}
      className="relative min-h-screen overflow-hidden bg-cream"
    >
      {/* Decorative blobs */}
      <span
        className="blob bg-coral-soft"
        style={{ width: 360, height: 360, top: -140, left: -140 }}
      />
      <span
        className="blob bg-coral-soft"
        style={{ width: 300, height: 300, bottom: -120, right: -120 }}
      />

      {/* Header / Logo */}
      <header className="relative z-10 border-b border-[var(--brand-line)]/60 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-6 py-2.5 flex items-center justify-center md:justify-between">
          <div data-testid={LANDING.logo} className="brand-mark">
            <img
              src={LOGO_URL}
              alt="Casal por São Paulo"
              className="h-10 md:h-11 w-auto block"
              loading="eager"
            />
            <span className="font-serif-brand text-[1rem] md:text-[1.1rem] font-medium tracking-tight text-ink hidden sm:inline-block">
              Casal por São Paulo
            </span>
          </div>
          <span className="hidden md:inline-flex text-[11px] uppercase tracking-[0.18em] text-ink-soft font-sans-brand">
            Experiências que criam histórias
          </span>
        </div>
      </header>

      {/* Hero — single centered column */}
      <section className="relative z-10 max-w-5xl mx-auto px-6 pt-5 md:pt-7 pb-12 text-center">
        <p className="fade-in-up font-sans-brand text-[10.5px] md:text-[11px] uppercase tracking-[0.22em] text-coral font-semibold mb-3">
          ✈ Roteiro exclusivo — China
        </p>

        <h1
          data-testid={LANDING.headline}
          className="fade-in-up delay-1 font-serif-brand text-[1.35rem] sm:text-[1.7rem] md:text-[1.95rem] lg:text-[2.15rem] leading-[1.18] font-light text-ink mx-auto max-w-4xl"
          style={{ letterSpacing: "-0.012em" }}
        >
          Como se hospedar em{" "}
          <em className="not-italic text-coral font-normal">
            hotéis 5 estrelas na China
          </em>{" "}
          pagando menos do que você pagaria em hotéis comuns nos Estados Unidos
          ou na Europa.
        </h1>

        <p
          data-testid={LANDING.subheadline}
          className="fade-in-up delay-2 mt-3.5 font-sans-brand text-[13.5px] md:text-[14.5px] leading-relaxed text-ink-soft max-w-2xl mx-auto"
        >
          Sem falar mandarim, sem ficar sem internet, sem errar na logística da
          viagem e sem perder dinheiro com decisões que poderiam ser evitadas.
        </p>

        {/* Audio cue bar — sits flush above the video */}
        <div
          data-testid={LANDING.videoSection}
          className="fade-in-up delay-3 mt-6 mx-auto w-full max-w-[920px]"
        >
          <div
            data-testid={LANDING.audioCue}
            className="audio-cue-bar"
          >
            <Volume2 size={16} strokeWidth={2.5} className="shrink-0" />
            <span className="truncate">
              Ative o som do seu dispositivo e clique no botão “play” abaixo —
              Assista agora!
            </span>
          </div>

          <VTurbPlayer testId={LANDING.vturbPlayer} />
        </div>

        {/* Below-video copy */}
        <p className="fade-in-up mt-6 font-sans-brand text-[12.5px] md:text-[13.5px] uppercase tracking-[0.08em] text-ink-soft max-w-3xl mx-auto leading-relaxed">
          Assista ao vídeo para entender:{" "}
          <span className="text-ink font-semibold normal-case">
            por que a maioria das pessoas acredita que viajar para a China é
            complicado
          </span>
          , quando na verdade o problema é não ter as informações certas antes
          de embarcar.
        </p>

        {/* Sub-form copy + CTA — appears after 30s via opacity to avoid layout shift */}
        <div
          className="mt-7 max-w-[920px] mx-auto transition-opacity duration-700"
          style={{ opacity: showCta ? 1 : 0, pointerEvents: showCta ? "auto" : "none" }}
          aria-hidden={!showCta}
        >
          <p
            data-testid={LANDING.formIntro}
            className="font-serif-brand italic text-[17px] md:text-[19px] text-ink mb-3 leading-snug font-normal"
          >
            Pronto para descobrir o caminho certo?
            <span className="not-italic font-sans-brand text-[13px] md:text-[14px] text-ink-soft block mt-2 max-w-xl mx-auto leading-relaxed">
              Deixe seus dados abaixo e receba o roteiro completo para planejar
              sua viagem à China com segurança e economia.
            </span>
          </p>

          <button
            data-testid={LANDING.ctaPrimary}
            onClick={openForm}
            className="cta-bar font-sans-brand"
          >
            Quero planejar minha viagem para a China com mais segurança
          </button>

          <p className="mt-4 text-[12px] text-ink-soft font-sans-brand">
            + de 80 mil pessoas já transformaram suas experiências de viagem com
            a gente.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer
        data-testid={LANDING.footer}
        className="relative z-10 border-t border-[var(--brand-line)]/70 bg-[var(--brand-cream-2)]/60"
      >
        <div className="max-w-6xl mx-auto px-6 py-8 flex flex-col items-center justify-center gap-3">
          <div className="brand-mark">
            <img
              src={LOGO_URL}
              alt="Casal por São Paulo"
              className="h-9 w-auto block"
              loading="lazy"
            />
            <span className="font-serif-brand text-[1rem] font-medium text-ink">
              Casal por São Paulo
            </span>
          </div>
          <p className="text-xs text-ink-soft font-sans-brand text-center">
            © {new Date().getFullYear()} Casal por São Paulo. Todos os direitos
            reservados.
          </p>
        </div>
      </footer>

      <LeadDialog
        open={open}
        onOpenChange={setOpen}
        triggerTestId={LANDING.leadDialogOpen}
      />
    </main>
  );
}
